import { defineStore } from "pinia";
import {
  type ImageFit,
  type ReaderSettings,
  type ReadingDirection,
  type ReadingMode,
  SettingsTemplate,
  type TitleReadingMode,
} from "~/src/api";
import { useSettingsStore } from "~/stores/settings";
import naturalEval from "~/src/naturalEval";
import { profileMap } from "~/utils/static";
import deepEqual from "deep-eql";
import { diff } from "deep-diff";
import cloneDeep from "clone-deep";
import type { WatchStopHandle } from "vue";
import {
  AvailablePresets,
  getDefaultReaderSettings,
  type PartialReaderSettings,
  PresetMap,
  type ReaderSettingsInterface,
} from "~/src/reader/static";
import { debounce } from "perfect-debounce";

const debug = (...args: any[]) => {
  console.debug(
    "%c%s",
    "color:black;background-color:#ff9999;padding:2px 4px;border-radius:4px;",
    "Reader Settings",
    ...args,
  );
};

const state = () => ({
  // reader settings
  currentProfileIndex: 0,
  readingMode: "single" as ReadingMode,
  readingDirection: "content" as ReadingDirection,
  scrollDirection: "horizontal" as "horizontal" | "vertical",
  imageFit: { custom: { maximumWidth: 0.75 }, current: "custom" as ImageFit },
  continuous: false,
  upscaleSmallPages: true,
  animation: true,
  scrollWheelBehavior: "pageTurn" as "pageTurn" | "zoom" | "none",
  hideReadingHint: false,
  ...getDefaultReaderSettings(),

  // unsaved + states
  titleReadingMode: "ltr" as TitleReadingMode,
  _immersionBreak: true,
  _isZoomed: false,
  _stopHandles: [] as WatchStopHandle[],
  navigationGuide: true,
  menuOpen: false,
  commentsOpen: false,
  lastOpenedDrawer: null as "menu" | "comments" | null,
  error: null as null | any,
  preloadPending: false,
  currentProfile: null as null | ReaderSettings[number],
  hasChangesToDefault: false,
});

const onSettingsUpdateHooks: (() => void)[] = [];

export const useReaderSettings = defineStore("reader-store", {
  state,
  actions: {
    async initialize() {
      const startTime = Date.now();
      const settingsStore = useSettingsStore();
      await settingsStore.waitForInitialization();
      debug("Settings initialized in", Date.now() - startTime, "ms");

      this.addWatchers();
    },
    addWatchers() {
      this._stopHandles.push(
        watch(
          () => this.menuOpen,
          (v) => {
            if (v && this.lastOpenedDrawer !== "menu")
              this.lastOpenedDrawer = "menu";
            setTimeout(this.updateSettings, 1000);
          },
        ),
      );
      this._stopHandles.push(
        watch(
          () => this.commentsOpen,
          (v) => {
            if (v && this.lastOpenedDrawer !== "comments")
              this.lastOpenedDrawer = "comments";
            setTimeout(this.updateSettings, 1000);
          },
        ),
      );
      this._stopHandles.push(
        watch(
          () => this.readingDirection,
          (v) => {
            if (v === "content") {
              this.changeToContent();
            } else if (v === "ltr") {
              this.changeToLtr();
            } else if (v === "rtl") {
              this.changeToRtl();
            }
            setTimeout(this.updateSettings, 1000);
          },
        ),
      );

      const debouncedUpdate = debounce(this.updateSettings, 300);

      this._stopHandles.push(
        watch(
          () => [
            this.currentProfileIndex,
            this.readingMode,
            this.readingDirection,
            this.scrollDirection,
            JSON.stringify(this.imageFit),
            this.continuous,
            this.upscaleSmallPages,
            this.animation,
            this.scrollWheelBehavior,
            this.hideReadingHint,
          ],
          () => setTimeout(debouncedUpdate),
        ),
      );
    },
    changeToLtr() {
      const settingsStore = useSettingsStore();

      this.readingDirection = "ltr";
      settingsStore.settings.reader[this.currentProfileIndex].readingDirection =
        "ltr";
    },
    changeToRtl() {
      const settingsStore = useSettingsStore();

      this.readingDirection = "rtl";
      settingsStore.settings.reader[this.currentProfileIndex].readingDirection =
        "rtl";
    },
    changeToContent() {
      const settingsStore = useSettingsStore();

      this.readingDirection = "content";
      settingsStore.settings.reader[this.currentProfileIndex].readingDirection =
        "content";
    },
    applyPreset(preset: AvailablePresets) {
      const template = PresetMap.get(preset);
      if (!template) {
        console.warn("reader preset not found");
        return;
      }

      this.$patch({
        ...template,
        imageFit: {
          ...this.imageFit,
          ...template.imageFit,
        },
      });

      this.updateSettings();
    },
    applySettings(settings: PartialReaderSettings) {
      this.readingMode = settings.readingMode ?? this.readingMode;
      this.imageFit = {
        custom: settings.imageFit?.custom ?? this.imageFit.custom,
        current: settings.imageFit?.current ?? this.imageFit.current,
      };
      this.readingDirection =
        settings.readingDirection ?? this.readingDirection;
      this.scrollDirection = settings.scrollDirection ?? this.scrollDirection;
      this.scrollWheelBehavior =
        settings.scrollWheelBehavior ?? this.scrollWheelBehavior;
      this.upscaleSmallPages =
        settings.upscaleSmallPages ?? this.upscaleSmallPages;
      this.animation = settings.animation ?? this.animation;
      this.continuous = settings.continuous ?? this.continuous;
      this.hideReadingHint = settings.hideReadingHint ?? this.hideReadingHint;
    },
    applyProfile(
      profile: string,
      ctx = {
        "title.readingMode": "ltr" as TitleReadingMode,
        "screen.width": window.innerWidth,
      },
    ) {
      const presets = this.getAvailableProfiles(ctx);
      const profileIndex = Math.max(
        0,
        presets.findIndex((p) => p.profile === profile),
      );
      const profileSettings = presets[profileIndex];
      if (!profileSettings) return;

      this.currentProfileIndex = profileIndex;
      this.currentProfile = profileSettings;
      this.applySettings(profileSettings);
    },
    updateSettings() {
      update(this.$state);
    },
    purge() {
      while (this._stopHandles.length > 0) {
        this._stopHandles.pop()?.apply(null);
      }

      this.$reset();
    },
    onSettingsUpdate: (hook: () => void) => {
      onSettingsUpdateHooks.push(hook);
    },

    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      if (this.menuOpen) this.lastOpenedDrawer = "menu";
    },
    toggleComments() {
      this.commentsOpen = !this.commentsOpen;
      if (this.commentsOpen) this.lastOpenedDrawer = "comments";
    },
    openMenu() {
      this.menuOpen = true;
      this.lastOpenedDrawer = "menu";
    },
    openComments() {
      this.commentsOpen = true;
      this.lastOpenedDrawer = "comments";
    },

    getAvailableProfiles(
      ctx = {
        "title.readingMode": "ltr" as TitleReadingMode,
        "screen.width": window.innerWidth,
      },
    ) {
      const settingsStore = useSettingsStore();
      const profiles = settingsStore.settings.reader;

      return profiles.filter((setting) =>
        setting.listCondition ? naturalEval(setting.listCondition, ctx) : true,
      );
    },

    resetProfile(profile: string) {
      const profileSettings = profileMap[profile];
      if (profileSettings) {
        this.currentProfile = profileSettings;
        this.applySettings(profileSettings);
      }
    },

    getMatchingPresets() {
      const matching: AvailablePresets[] = [];
      const presets = PresetMap.entries();

      for (const [preset, store] of presets) {
        // a basic one directional matcher
        let match = true;
        for (const [key, value] of Object.entries(store)) {
          if (value !== this[key as keyof ReaderSettingsInterface]) {
            match = false;
            break;
          }
        }

        if (match) matching.push(preset);
      }

      return matching;
    },
  },
  getters: {
    immersionBreak(state) {
      return state._immersionBreak && !state._isZoomed;
    },
    currentPreset(state) {
      const presets = PresetMap.entries();

      for (const preset of presets) {
        let allMatch = true;
        for (const key in preset[1]) {
          if (
            state[key as keyof typeof state] !==
            preset[1][key as keyof (typeof preset)[1]]
          ) {
            allMatch = false;
            break;
          }
        }
        if (allMatch) return preset[0];
      }
      return "custom";
    },
  },
});

let shouldNotify = true;

function update(readerState: ReturnType<typeof state>) {
  const settingsStore = useSettingsStore();
  const currentTemplate = new SettingsTemplate({
    data: settingsStore.settings,
  });

  const initial = cloneDeep(currentTemplate.template);

  const ctx = {
    "title.readingMode": readerState.titleReadingMode,
    "screen.width": window.innerWidth,
  };

  // Our currentProfileIndex is an index of a filtered list, so we need to find the real index in the whole list
  const currentProfileName = settingsStore.settings.reader.filter((setting) =>
    setting.listCondition ? naturalEval(setting.listCondition, ctx) : true,
  )[readerState.currentProfileIndex].profile;

  debug("Updating ", currentProfileName);

  const currentProfile = settingsStore.settings.reader.find(
    (setting) => setting.profile === currentProfileName,
  )!;

  currentTemplate.setReadingMode(currentProfileName, readerState.readingMode);
  currentTemplate.setScrollDirection(
    currentProfileName,
    readerState.scrollDirection,
  );
  currentTemplate.setDirection(
    currentProfileName,
    readerState.readingDirection,
  );

  currentTemplate.setUpscaleSmallPages(
    currentProfileName,
    readerState.upscaleSmallPages,
  );
  currentTemplate.setCurrentImageFit(
    currentProfileName,
    readerState.imageFit.current,
  );
  currentTemplate.setImageFitCustomValues(
    currentProfileName,
    readerState.imageFit.custom,
  );

  currentTemplate.setAnimation(currentProfileName, readerState.animation);
  currentTemplate.setContinuous(currentProfileName, readerState.continuous);

  currentTemplate.setScrollWheelBehavior(
    currentProfileName,
    readerState.scrollWheelBehavior,
  );

  currentTemplate.setHideReadingHint(
    currentProfileName,
    readerState.hideReadingHint,
  );

  if (profileMap.hasOwnProperty(currentProfileName)) {
    readerState.hasChangesToDefault = !deepEqual(
      currentProfile,
      profileMap[currentProfileName],
    );
  }

  // We check the difference so far.
  // We don't want to show the settings saved notification when only the reader menu has been opened/closed
  // That's why we check the difference right now to tell whether any other settings have been changed,
  // and update the reader menu visibility for this profile later
  const difference = diff(currentTemplate.template, initial);
  shouldNotify = !!difference;

  currentTemplate.setReaderMenuOpenOnPageLoad(
    currentProfileName,
    readerState.menuOpen,
  );

  queue(async () => settingsStore.update(currentTemplate.template.data));
}

let hasInstance = false;
let stopSignal = false;
let queueArray: (() => Promise<void>)[] = [];

async function queue(fn: () => Promise<void>) {
  queueArray.push(fn);

  if (hasInstance) return;
  hasInstance = true;

  let isRunning = false;

  const timer = setInterval(async () => {
    if (stopSignal) {
      clearInterval(timer);
      return;
    }

    if (isRunning) return;

    if (queueArray.length > 0) {
      isRunning = true;
      const takenSplice = queueArray.splice(0);
      await takenSplice[takenSplice.length - 1]();
      isRunning = false;

      if (shouldNotify) {
        onSettingsUpdateHooks.forEach((hook) => hook());
      }
    }
  }, 500);
}
